export const FeatureFlagKey = {
  // Per-provider integration gates. Convention: INTEGRATION_<SLUG-UPPER>, one
  // per CRM adapter in the core integrations registry. The core hub resolves
  // these server-side (forwarding the caller's token) to decide which providers
  // are listed/connectable — fail-closed, so a gated provider stays hidden and
  // its connect endpoints reject until the flag is enabled. Core references the
  // key strings by the same convention; keep the two in sync.
  INTEGRATION_ACCULYNX: 'INTEGRATION_ACCULYNX',
  INTEGRATION_ROOFLINK: 'INTEGRATION_ROOFLINK',
  INTEGRATION_SERVICETITAN: 'INTEGRATION_SERVICETITAN',
  INTEGRATION_WEBHOOK: 'INTEGRATION_WEBHOOK',
  INTEGRATION_HATCH: 'INTEGRATION_HATCH',
  INTEGRATION_SLACK: 'INTEGRATION_SLACK',
  AUTOMATIONS: 'AUTOMATIONS',
  FORMS: 'FORMS',
} as const;

export type FeatureFlagKey = (typeof FeatureFlagKey)[keyof typeof FeatureFlagKey];

/** The unit a flag's rollout and overrides operate on. */
export type FeatureFlagScope = 'user' | 'workspace';

export interface FeatureFlagDefinition {
  key: FeatureFlagKey;
  /** Human-friendly title shown in the admin UI. */
  title: string;
  /** Human-friendly description of what the flag controls. */
  description: string;
  /**
   * Whether the flag targets individual users or whole workspaces. This is the
   * code-defined default; the persisted `feature_flags` row's scope is what the
   * resolver uses (and is editable in the admin), but a row is seeded from this.
   */
  scope: FeatureFlagScope;
}

/**
 * The catalog of feature flags, defined in code alongside the key set. This is
 * the source of truth for which flags exist and their friendly
 * title/description.
 *
 * A `feature_flags` DB row is lazy-created only when a flag is actually used
 * (toggled on, or a per-user scope is created for it). Until then the flag
 * still appears in the admin table sourced entirely from this catalog. The DB
 * row, once it exists, may override the title/description per environment.
 */
export const FEATURE_FLAG_DEFINITIONS: Record<FeatureFlagKey, FeatureFlagDefinition> = {
  [FeatureFlagKey.INTEGRATION_ACCULYNX]: {
    key: FeatureFlagKey.INTEGRATION_ACCULYNX,
    title: 'Integration: AccuLynx',
    description:
      'Lists the AccuLynx CRM provider in the Integration Hub and permits connecting it. Gated server-side (fail-closed).',
    scope: 'user',
  },
  [FeatureFlagKey.INTEGRATION_ROOFLINK]: {
    key: FeatureFlagKey.INTEGRATION_ROOFLINK,
    title: 'Integration: RoofLink',
    description:
      'Lists the RoofLink CRM provider in the Integration Hub and permits connecting it. Gated server-side (fail-closed).',
    scope: 'user',
  },
  [FeatureFlagKey.INTEGRATION_SERVICETITAN]: {
    key: FeatureFlagKey.INTEGRATION_SERVICETITAN,
    title: 'Integration: ServiceTitan',
    description:
      'Lists the ServiceTitan CRM provider in the Integration Hub and permits connecting it. Gated server-side (fail-closed).',
    scope: 'user',
  },
  [FeatureFlagKey.INTEGRATION_WEBHOOK]: {
    key: FeatureFlagKey.INTEGRATION_WEBHOOK,
    title: 'Integration: Webhook',
    description:
      'Lists the generic Webhook ingestion provider in the Integration Hub and permits connecting it. Gated server-side (fail-closed).',
    scope: 'user',
  },
  [FeatureFlagKey.INTEGRATION_HATCH]: {
    key: FeatureFlagKey.INTEGRATION_HATCH,
    title: 'Integration: Hatch AI',
    description:
      'Lists the Hatch AI ingestion provider in the Integration Hub and permits connecting it. Gated server-side (fail-closed).',
    scope: 'user',
  },
  [FeatureFlagKey.INTEGRATION_SLACK]: {
    key: FeatureFlagKey.INTEGRATION_SLACK,
    title: 'Integration: Slack',
    description:
      'Lists the Slack communication provider in the Integration Hub and permits connecting it. Outbound only — an automation Action posts a message to a channel. Gated server-side (fail-closed).',
    scope: 'workspace',
  },
  [FeatureFlagKey.AUTOMATIONS]: {
    key: FeatureFlagKey.AUTOMATIONS,
    title: 'Automations',
    description:
      'The workspace Automations dialog (workspace-selector entry): the Manage list, the create flow, and the React Flow builder. Off hides the entry point entirely while the feature is built out.',
    scope: 'workspace',
  },
  [FeatureFlagKey.FORMS]: {
    key: FeatureFlagKey.FORMS,
    title: 'Forms',
    description:
      'Settings → Forms (build, publish, share) and the Project Forms tab (fill, read, share, delete submissions). Off hides every Forms entry point while the feature is built out.',
    scope: 'workspace',
  },
};

/** Convenience array form of {@link FEATURE_FLAG_DEFINITIONS}. */
export const FEATURE_FLAG_DEFINITION_LIST: FeatureFlagDefinition[] =
  Object.values(FEATURE_FLAG_DEFINITIONS);
